import { useMemo, useRef, useState, type RefObject } from "react";
import { useMutation } from "convex/react";
import {
  ArrowBendUpLeft,
  ArrowsDownUp,
  Broadcast,
  CaretDown,
  CaretRight,
  Check,
  CheckCircle,
  EyeSlash,
  HourglassMedium,
  Plus,
  Sparkle,
  User,
  Users,
  Warning,
  X,
} from "@phosphor-icons/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  AdminAudienceState,
  RoomThread,
} from "../../shared/types.ts";
import type { RoleAuth } from "../lib/auth.ts";
import {
  deriveBoardStrata,
  type BoardReplyRow,
  type BoardSort,
  type PendingRow,
} from "../lib/boardStrata.ts";
import { notificationCopy, staffPostQueuedCopy } from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";
import { AudiencePrivacyCue } from "../components/AudiencePrivacyCue.tsx";
import type { PendingAudienceIds } from "../lib/optimisticAudience.ts";
import { fmtAgo, isOnAir, replyAuthorDisplay } from "../lib/threads.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { ThreadRow, type ThreadActions } from "../components/ThreadRow.tsx";

type AdminBoardSort = "oldest" | "newest" | "upvotes" | "replies";

function selectedBoardSort(sort: AdminBoardSort): BoardSort {
  switch (sort) {
    case "upvotes":
      return { key: "upvotes", dir: "desc" };
    case "replies":
      return { key: "replies", dir: "desc" };
    case "newest":
      return { key: "latest", dir: "desc" };
    case "oldest":
    default:
      return { key: "latest", dir: "asc" };
  }
}

function queuedReplyCount(thread: RoomThread): number {
  return (thread.replies ?? []).filter((r) => r.status === "queued").length;
}

/** Center workspace: stratified moderation board (PENDING / ON BOARD / ANSWERED /
 *  HIDDEN) with a non-scrolling header (projector strip · stats · staff composer)
 *  above a scrolling strata region. Reuses ThreadRow for row bodies. */
export function StratifiedBoard({
  auth,
  sessionId,
  state,
  actions,
  pending,
  joinedCount,
}: {
  auth: RoleAuth;
  sessionId: string;
  state: AdminAudienceState;
  actions: ThreadActions;
  pending: PendingAudienceIds;
  joinedCount: number;
}) {
  const createStaffPost = useMutation(convexApi.audience.createStaffPost);
  const [staffDraft, setStaffDraft] = useState("");
  const [boardSort, setBoardSort] = useState<AdminBoardSort>("oldest");

  const projector = state.projector;
  const strata = useMemo(
    () => deriveBoardStrata(state.threads, selectedBoardSort(boardSort)),
    [state.threads, boardSort],
  );

  // On-air pinning: the projected thread floats to the top of ON BOARD.
  const projectedId = projector?.mode === "thread" ? projector.projectedThreadId : undefined;
  const onBoard = useMemo(() => {
    if (!projectedId) return strata.onBoard;
    const idx = strata.onBoard.findIndex((t) => t.id === projectedId);
    if (idx <= 0) return strata.onBoard;
    const copy = [...strata.onBoard];
    const [pinned] = copy.splice(idx, 1);
    return [pinned, ...copy];
  }, [strata.onBoard, projectedId]);

  const stats = { ...strata.stats, joined: joinedCount };

  const pendingRef = useRef<HTMLDivElement>(null);
  const onboardRef = useRef<HTMLDivElement>(null);
  const answeredRef = useRef<HTMLDetailsElement>(null);
  const hiddenRef = useRef<HTMLDetailsElement>(null);
  function jumpTo<T extends HTMLElement>(ref: RefObject<T | null>) {
    const el = ref.current;
    if (!el) return;
    // Answered/Hidden are collapsed <details>; open before scrolling so the
    // jump lands on visible content instead of a collapsed summary.
    if (el instanceof HTMLDetailsElement) el.open = true;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function queueStaffPost() {
    const body = staffDraft.trim();
    if (!body) return;
    try {
      await createStaffPost({ auth, sessionId: sessionId as Id<"sessions">, body });
      setStaffDraft("");
      notify.success(staffPostQueuedCopy(body));
    } catch {
      notify.error(notificationCopy.admin.couldNotQueue);
    }
  }

  function threadCard(thread: RoomThread, variant: "pending" | "board") {
    const onAir = isOnAir(thread, projector);
    const queued = queuedReplyCount(thread);
    const isAi = thread.threadOrigin === "ai";
    const cls = variant === "board"
      ? `card board${onAir ? " onair" : ""}${isAi ? " is-ai" : ""}`
      : "card pending";
    const showLead = onAir || (variant === "board" && queued > 0);
    return (
      <div key={thread.id} className={cls} data-testid="board-card" data-thread-id={thread.id}>
        {showLead && (
          <div className="card-lead">
            {onAir && (
              <span className="onair-flag"><Broadcast weight="fill" /> On air</span>
            )}
            {variant === "board" && queued > 0 && (
              <span className="reply-pending-chip" data-testid="reply-pending-chip">
                <Warning /> {queued} {queued === 1 ? "reply" : "replies"} pending
              </span>
            )}
          </div>
        )}
        <ThreadRow
          thread={thread}
          categories={state.categories}
          onAir={onAir}
          actions={actions}
          pending={pending.threadIds.has(thread.id)}
          pendingReplyIds={pending.replyIds}
          projectorPending={pending.projector}
        />
      </div>
    );
  }

  function pendingReplyCard(row: BoardReplyRow) {
    const busy = pending.replyIds.has(row.reply.id);
    const who = replyAuthorDisplay(
      row.reply.authorLabel,
      row.reply.authorIdentityKind,
    ).label;
    return (
      <div
        key={`reply-${row.reply.id}`}
        className="card pending card-reply"
        data-testid="board-card"
        data-reply-id={row.reply.id}
      >
        <div className="card-top">
          <span className="src reply"><ArrowBendUpLeft /> Reply</span>
          <div className="card-body">
            {/* clamped to 2 lines via CSS; title keeps the full parent readable */}
          <div className="parent-snip" title={row.parent.body}>on: &ldquo;{row.parent.body}&rdquo;</div>
            <div className="text">{row.reply.body}</div>
            <div className="card-meta">
              <span className="ts">{fmtAgo(row.reply.createdAt)}</span>
              {who && <span className="mini">by {who}</span>}
            </div>
          </div>
          <div className="card-actions">
            <button
              type="button"
              className="btn good sm"
              data-testid="reply-approve-btn"
              aria-label="Approve reply"
              disabled={busy}
              onClick={() => actions.approveReply(row.reply.id)}
            >
              <Check weight="bold" /> Approve
            </button>
            <button
              type="button"
              className="btn danger-ghost sm"
              data-testid="reply-reject-btn"
              aria-label="Hide reply"
              disabled={busy}
              onClick={() => actions.hideReply(row.reply.id)}
            >
              <X weight="bold" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  function pendingRow(row: PendingRow) {
    return "kind" in row ? pendingReplyCard(row) : threadCard(row, "pending");
  }

  return (
    <div className="admin2-board">
      <div className="board-header">
        <AudiencePrivacyCue policy={state.policy} transition={state.identityTransition} />
        <div className="stats">
          <button
            type="button"
            className="stat-pill"
            data-testid="board-stat-pending"
            title="Jump to pending"
            onClick={() => jumpTo(pendingRef)}
          >
            <HourglassMedium weight="fill" /> <b>{stats.pending}</b> pending
            <span className="brk">
              <span><Sparkle weight="fill" className="i-ai" />{stats.pendingAi}</span>
              <span><User weight="fill" className="i-aud" />{stats.pendingAudience}</span>
              <span><ArrowBendUpLeft className="i-reply" />{stats.pendingReplies}</span>
            </span>
          </button>
          <span className="sdiv" />
          <button
            type="button"
            className="stat-lite"
            data-testid="board-stat-onboard"
            title="Jump to on board"
            onClick={() => jumpTo(onboardRef)}
          >
            <Broadcast weight="fill" className="i-aud" /> <b>{stats.onBoard}</b> on board{" "}
            <span className="dim">{stats.onBoardAi}·{stats.onBoardAudience}</span>
          </button>
          <span className="sdiv" />
          <span className="stat-lite" data-testid="board-stat-replies">
            <ArrowBendUpLeft className="i-mut" /> <b>{stats.replies}</b> replies{" "}
            <span className="dim">{stats.pendingReplies} pending</span>
          </span>
          <span className="sdiv" />
          <button
            type="button"
            className="stat-lite"
            data-testid="board-stat-answered"
            title="Jump to answered"
            onClick={() => jumpTo(answeredRef)}
          >
            <CheckCircle weight="fill" className="i-ok" /> <b>{stats.answered}</b> answered
          </button>
          <span className="sdiv" />
          <button
            type="button"
            className="stat-lite"
            data-testid="board-stat-hidden"
            title="Jump to hidden"
            onClick={() => jumpTo(hiddenRef)}
          >
            <EyeSlash className="i-mut" /> <b>{stats.hidden}</b> hidden
          </button>
          <span className="sdiv" />
          <span className="stat-lite" data-testid="board-stat-joined">
            <Users weight="fill" className="i-mut" /> <b>{stats.joined}</b> joined
          </span>
        </div>

        <div className="composer">
          <input
            aria-label="Add a staff post to the queue"
            placeholder="Add a staff post to the queue…"
            data-testid="board-staff-input"
            value={staffDraft}
            onChange={(e) => setStaffDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void queueStaffPost(); }}
          />
          <button
            type="button"
            className="btn"
            data-testid="board-staff-queue"
            disabled={!staffDraft.trim()}
            onClick={() => void queueStaffPost()}
          >
            <Plus /> Queue
          </button>
        </div>
      </div>

      <div className="board-scroll">
        <div ref={pendingRef} className="stratum-head st-pending" data-testid="stratum-pending-head">
          <CaretDown className="caret-s" /><span className="st-name">Pending</span>
          <span className="st-count">{strata.pending.length}</span>
          <span className="st-sort">oldest first · triage in order</span>
        </div>
        <div className="lane" data-testid="stratum-pending">
          {strata.pending.length === 0
            ? <EmptyState size="sm" testid="stratum-pending-empty">No threads waiting.</EmptyState>
            : strata.pending.map(pendingRow)}
        </div>

        <div ref={onboardRef} className="stratum-head st-board" data-testid="stratum-onboard-head">
          <CaretDown className="caret-s" /><span className="st-name">On board</span>
          <span className="st-count">{onBoard.length}</span>
          <label className="st-sort-control">
            <ArrowsDownUp weight="bold" aria-hidden="true" />
            <span>Sort</span>
            <select
              aria-label="Sort on-board threads"
              data-testid="admin-board-sort"
              value={boardSort}
              onChange={(event) => setBoardSort(event.target.value as AdminBoardSort)}
            >
              <option value="oldest">Oldest</option>
              <option value="newest">Newest</option>
              <option value="upvotes">Most upvotes</option>
              <option value="replies">Most replies</option>
            </select>
          </label>
        </div>
        <div className="lane" data-testid="stratum-onboard">
          {onBoard.length === 0
            ? <EmptyState size="sm" testid="stratum-onboard-empty">No approved threads yet.</EmptyState>
            : onBoard.map((t) => threadCard(t, "board"))}
        </div>

        <details ref={answeredRef} className="stratum-details" data-testid="stratum-answered-details">
          <summary className="stratum-head st-answered">
            <CaretRight className="caret-s caret-closed" />
            <CaretDown className="caret-s caret-open" />
            <span className="st-name">Answered</span>
            <span className="st-count">{strata.answered.length}</span>
            <span className="st-sort">collapsed · session order</span>
          </summary>
          <div className="lane" data-testid="stratum-answered">
            {strata.answered.map((t) => threadCard(t, "board"))}
          </div>
        </details>

        <details ref={hiddenRef} className="stratum-details" data-testid="stratum-hidden-details">
          <summary className="stratum-head st-hidden">
            <CaretRight className="caret-s caret-closed" />
            <CaretDown className="caret-s caret-open" />
            <span className="st-name">Hidden</span>
            <span className="st-count">{strata.hidden.length}</span>
            <span className="st-sort">collapsed · reopen available</span>
          </summary>
          <div className="lane" data-testid="stratum-hidden">
            {strata.hidden.map((t) => threadCard(t, "board"))}
          </div>
        </details>
      </div>
    </div>
  );
}
